import { useId, useState, type FormEventHandler, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

import { tokens } from '@configuration/theme'
import EyeIcon from '@icons/Eye.icon'
import EyeOffIcon from '@icons/EyeOff.icon'
import Field, { fieldControlSx, fieldErrorId } from '@base/Field/Field.component'

export interface TextFieldProps {
  id?: string
  name?: string
  type?: string
  /** Field label rendered above the input. */
  label?: ReactNode
  /** Right-aligned slot in the label row (e.g. a "Forgot?" link). */
  hint?: ReactNode
  /** Leading icon inside the input frame. */
  icon?: ReactNode
  /** Validation error — switches the frame to the red skin and shows the message. */
  error?: string | null
  /** Helper text shown below the field when there is no error. */
  desc?: ReactNode
  placeholder?: string
  /** Uncontrolled value — Kratos forms render with `defaultValue`. */
  defaultValue?: string
  value?: string
  required?: boolean
  autoComplete?: string
  autoFocus?: boolean
  readOnly?: boolean
  inputMode?: 'text' | 'numeric' | 'tel' | 'email' | 'search' | 'url' | 'none' | 'decimal'
  onChange?: FormEventHandler<HTMLInputElement>
  onInput?: FormEventHandler<HTMLInputElement>
  'aria-label'?: string
  dataTestId?: string
  /** Extra styles merged onto the <input>. */
  inputSx?: SxProps<Theme>
}

// Presentational text input matching the design's `.field`. Uncontrolled by
// default (forwards `defaultValue`/`name`/`required`/`autoComplete`/`id`) so it
// drops into the existing Kratos forms without changing submit behaviour. When
// `type='password'` it renders an inline show/hide eye toggle.
const TextField = ({
  id,
  name,
  type = 'text',
  label,
  hint,
  icon,
  error,
  desc,
  placeholder,
  defaultValue,
  value,
  required,
  autoComplete,
  autoFocus,
  readOnly,
  inputMode,
  onChange,
  onInput,
  inputSx,
  'aria-label': ariaLabel,
  dataTestId = 'text-field',
}: TextFieldProps) => {
  const intl = useIntl()
  const [shown, setShown] = useState(false)
  const isPassword = type === 'password'
  const resolvedType = isPassword && shown ? 'text' : type
  // Fall back to a generated id so the <label> stays associated with the input
  // even when the caller doesn't supply one (login email/password fields).
  const reactId = useId()
  const fieldId = id ?? reactId
  const errorId = error ? fieldErrorId(fieldId) : undefined

  return (
    <Field
      id={fieldId}
      dataTestId={dataTestId}
      label={label}
      hint={hint}
      icon={icon}
      error={error}
      desc={desc}
      readOnly={readOnly}
      trailing={
        isPassword ? (
          <Box
            component='button'
            type='button'
            onClick={() => setShown((v) => !v)}
            aria-label={intl.formatMessage({
              id: shown ? 'common.password.hide' : 'common.password.show',
            })}
            sx={{
              appearance: 'none',
              border: 0,
              background: 'transparent',
              padding: '0 12px',
              height: '100%',
              color: tokens.businessBlack60,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              '&:hover': { color: tokens.businessBlack },
              '&:focus-visible': {
                outline: `2px solid ${tokens.versatileViolet}`,
                outlineOffset: -2,
              },
            }}
          >
            {shown ? <EyeOffIcon /> : <EyeIcon />}
          </Box>
        ) : undefined
      }
    >
      <Box
        component='input'
        id={fieldId}
        name={name}
        type={resolvedType}
        placeholder={placeholder}
        defaultValue={defaultValue}
        value={value}
        required={required}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        readOnly={readOnly}
        inputMode={inputMode}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        data-testid={`${dataTestId}-input`}
        onChange={onChange}
        onInput={onInput}
        sx={[fieldControlSx, ...(Array.isArray(inputSx) ? inputSx : [inputSx])]}
      />
    </Field>
  )
}

export default TextField
